// hrpayroll/src/pages/Profile/ProfileTab.tsx
import { useRef, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "../../components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogTitle } from "../../components/ui/dialog";
import { useAuth } from "../../contexts/AuthContext";
import { useAccess } from "../../contexts/AccessContext";
import {
  updateMyProfile, uploadAvatar, removeAvatar, updateMyBasicProfile,
  updateMyPayoutBank, type PayoutBank,
} from "../../api/profile";
import type { MyProfile } from "./Profile";
import {
  BankNameCombobox,
  bankNameForIfsc,
  isKnownBankName,
} from "../../components/BankNameCombobox";

const INDIAN_STATES = [
  "Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Goa","Gujarat",
  "Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh",
  "Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland","Odisha","Punjab",
  "Rajasthan","Sikkim","Tamil Nadu","Telangana","Tripura","Uttar Pradesh",
  "Uttarakhand","West Bengal",
  "Andaman and Nicobar Islands","Chandigarh","Dadra and Nagar Haveli and Daman and Diu",
  "Delhi","Jammu and Kashmir","Ladakh","Lakshadweep","Puducherry",
];

const GENDER_OPTIONS = ["Male", "Female", "Other", "Prefer not to say"];

function toTitleCase(s?: string | null) {
  if (!s) return s;
  return s.replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
}

function isValidPhone(v: string) {
  return /^[6-9]\d{9}$/.test(v.replace(/\s/g, ""));
}

const EDITABLE = [
  "first_name", "last_name", "phone", "gender", "date_of_birth",
  "current_address1", "current_address2", "current_city", "current_state", "current_pin",
  "permanent_address1", "permanent_address2", "permanent_city", "permanent_state", "permanent_pin",
] as const;

// Bank Details — the account payouts are made FROM, plus the constants each bank
// prints on every row of a bulk-upload sheet. These prefill the salary and
// expense "Export Excel" dialogs, so an admin sets them once here instead of
// retyping an account number every payout run. Saved on the caller's User row
// (not the Employee row), which is why admins — who have no Employee record —
// can fill them in as well.
const BANK_FIELDS: { key: keyof PayoutBank; label: string; placeholder: string }[] = [
  { key: "bankName", label: "Bank name", placeholder: "e.g. AU Small Finance Bank" },
  { key: "accountNumber", label: "Account number / IBAN", placeholder: "Account payouts are debited from" },
  { key: "ifscCode", label: "IFSC code", placeholder: "e.g. AUBL0002345" },
  { key: "remitterName", label: "Remitter name", placeholder: "Printed on every AU sheet row" },
  { key: "senderAddress", label: "Sender address", placeholder: "e.g. DELHI (Kotak sheet column)" },
  { key: "paymentMethodName", label: "Payment method", placeholder: "e.g. NEFT" },
  { key: "paymentInstructions", label: "Payment instructions", placeholder: "e.g. Salary for the month" },
  { key: "ccy", label: "Currency (CCY)", placeholder: "INR" },
];

export default function ProfileTab({ profile, onSaved }: { profile: MyProfile; onSaved: () => void }) {
  const { setUserPhoto, setUserName } = useAuth();
  const { canManage } = useAccess();
  const [form, setForm] = useState<Record<string, any>>(
    Object.fromEntries(EDITABLE.map((k) => [k, (profile as any)[k] ?? ""])),
  );
  const [saving, setSaving] = useState(false);
  const [bank, setBank] = useState<PayoutBank>(
    Object.fromEntries(BANK_FIELDS.map((f) => [f.key, profile.payout_bank?.[f.key] ?? ""])),
  );
  const [photo, setPhoto] = useState<string | null>(profile.profile_photo_url ?? null);
  const [preview, setPreview] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  // Admins / head-office users have no Employee record (id is null) — show a
  // read-only profile: no avatar upload, no editable personal form, no Save.
  const canEdit = Boolean(profile.id);

  // The Bank Details card is for the person who RUNS the payouts, not for
  // everyone: it holds the account salaries and reimbursements are paid OUT
  // FROM and only exists to prefill the bank-sheet export dialogs. An employee's
  // own account — where they get PAID — is a different thing entirely and is
  // captured in Salary & Tax Declaration, not here. Gated on the Payroll
  // Approval panel rather than a role check, per the project's access rule: an
  // account with no Access profile (admin) gets it, staff granted it get it,
  // and ordinary employees / managers never see this card.
  const canHoldPayoutBank = canManage("payroll-approval");

  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const onPickAvatar = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file later
    if (!file) return;
    try {
      const { public_url } = await uploadAvatar(file);
      setPhoto(public_url);
      setUserPhoto(public_url); // update the top-bar dropdown immediately
      toast.success("Profile photo updated");
    } catch (err: any) {
      toast.error(err?.message || "Photo upload failed");
    }
  };

  const onRemoveAvatar = async () => {
    try {
      await removeAvatar();
      setPhoto(null);
      setUserPhoto(null); // clear the top-bar dropdown immediately
      toast.success("Profile photo removed");
    } catch (err: any) {
      toast.error(err?.message || "Could not remove photo");
    }
  };

  // ONE save for the whole tab — personal details and bank details go together,
  // so there's a single "Save changes" rather than a button per card. Accounts
  // without an Employee record (admins) save their name on the User row via the
  // basic endpoint; everyone's bank details always save the same way.
  const onSave = async () => {
    if (canHoldPayoutBank && !isKnownBankName(bank.bankName as string)) {
      toast.error("Please select the bank name from the list.");
      return;
    }
    setSaving(true);
    try {
      if (profile.id) {
        await updateMyProfile(profile.id, form);
      } else {
        const r = await updateMyBasicProfile({ first_name: form.first_name, last_name: form.last_name });
        if (r?.name) setUserName(r.name); // reflect in the top-bar immediately
      }
      if (canHoldPayoutBank) await updateMyPayoutBank(bank);
      toast.success("Profile updated");
      onSaved();
    } catch (err: any) {
      toast.error(err?.message || "Could not save profile");
    } finally {
      setSaving(false);
    }
  };

  const initial = (profile.name || profile.email || "U").charAt(0).toUpperCase();

  return (
    <div className="space-y-6 py-4">
      <Card>
        <CardContent className="flex items-center gap-4 py-5">
          <div className="relative">
            <button
              type="button"
              onClick={() => photo && setPreview(true)}
              className={`w-16 h-16 rounded-full overflow-hidden bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center text-white text-2xl font-semibold ${photo ? "cursor-zoom-in" : "cursor-default"}`}
              title={photo ? "View photo" : undefined}
            >
              {photo ? <img src={photo} alt="" className="w-full h-full object-cover" /> : initial}
            </button>
            {/* Avatar editing is available to EVERYONE (incl. admins without an
                Employee row — their photo is stored on the User row). */}
            <>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/png,image/jpeg,image/jpg"
                  className="hidden"
                  onChange={onPickAvatar}
                />
                <DropdownMenu>
                  <DropdownMenuTrigger className="absolute -bottom-1 -right-1 bg-white border border-gray-200 rounded-full px-2 py-0.5 text-[11px] cursor-pointer shadow-sm hover:bg-gray-50 focus:outline-none">
                    Edit
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-44">
                    {photo && (
                      <DropdownMenuItem onClick={() => setPreview(true)}>Preview photo</DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={() => fileRef.current?.click()}>
                      {photo ? "Change photo" : "Upload photo"}
                    </DropdownMenuItem>
                    {photo && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={onRemoveAvatar}
                          className="text-red-600 focus:text-red-600"
                        >
                          Remove photo
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
            </>
          </div>

          {photo && (
            <Dialog open={preview} onOpenChange={setPreview}>
              <DialogContent className="max-w-md p-0 overflow-hidden bg-transparent border-0 shadow-none">
                <DialogTitle className="sr-only">Profile photo</DialogTitle>
                <img src={photo} alt={profile.name || ""} className="w-full h-auto rounded-lg" />
              </DialogContent>
            </Dialog>
          )}
          <div>
            <p className="text-lg font-semibold text-gray-900">{profile.name}</p>
            <p className="text-sm text-gray-500">{profile.email}</p>
            <span className="inline-block mt-1 text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">
              {toTitleCase(profile.role)}
            </span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Employment</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <ReadOnly label="Employee ID" value={profile.employeeID} />
          <ReadOnly label="Designation" value={profile.designation} />
          <ReadOnly label="Role" value={toTitleCase(profile.role)} />
          <ReadOnly label="Branch" value={profile.branchName} />
          <ReadOnly label="Hire date" value={profile.hire_date} />
        </CardContent>
      </Card>

      {canEdit ? (
        <Card>
          <CardHeader><CardTitle className="text-base">Personal</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="First name" k="first_name" form={form} set={set} />
            <Field label="Last name" k="last_name" form={form} set={set} />
            <PhoneField label="Phone" k="phone" form={form} set={set} />
            <GenderField label="Gender" k="gender" form={form} set={set} />
            <Field label="Date of birth" k="date_of_birth" form={form} set={set} type="date" />
            <div className="sm:col-span-2 border-t border-gray-100 pt-2 mt-1 text-xs font-medium text-gray-400 uppercase">Current address</div>
            <AddressField label="Address line 1" k="current_address1" form={form} set={set} />
            <AddressField label="Address line 2" k="current_address2" form={form} set={set} />
            <Field label="City" k="current_city" form={form} set={set} />
            <StateField label="State" k="current_state" form={form} set={set} />
            <Field label="PIN" k="current_pin" form={form} set={set} />
            <div className="sm:col-span-2 border-t border-gray-100 pt-2 mt-1 text-xs font-medium text-gray-400 uppercase">Permanent address</div>
            <AddressField label="Address line 1" k="permanent_address1" form={form} set={set} />
            <AddressField label="Address line 2" k="permanent_address2" form={form} set={set} />
            <Field label="City" k="permanent_city" form={form} set={set} />
            <StateField label="State" k="permanent_state" form={form} set={set} />
            <Field label="PIN" k="permanent_pin" form={form} set={set} />
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader><CardTitle className="text-base">Personal</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="First name" k="first_name" form={form} set={set} />
              <Field label="Last name" k="last_name" form={form} set={set} />
            </div>
            <p className="text-xs text-gray-400">
              Other details (phone, address, etc.) are only available for accounts with an employee record.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Bank Details — payout side only, so it renders for whoever runs the
          exports (admin), never for ordinary staff. Saved on the User row, so
          it works for admins despite them having no Employee record. Nothing
          here is required: fill in only what your bank sheet needs. */}
      {canHoldPayoutBank && (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Bank Details</CardTitle>
          <p className="text-sm text-gray-500 mt-1">
            The account payouts are made from. Pre-fills the salary and expense
            bank upload sheets — every value stays editable at export time.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {BANK_FIELDS.map((f) => (
              <div key={f.key}>
                <Label className="text-xs text-gray-500">{f.label}</Label>
                {f.key === "bankName" ? (
                  <BankNameCombobox
                    value={(bank.bankName as string) ?? ""}
                    onChange={(name) => setBank((b) => ({ ...b, bankName: name }))}
                  />
                ) : (
                  <Input
                    value={(bank[f.key] as string) ?? ""}
                    placeholder={f.placeholder}
                    onChange={(e) => {
                      const v = e.target.value;
                      setBank((b) => {
                        const next = { ...b, [f.key]: v };
                        // A full IFSC names its bank — fill Bank name from it
                        // when it's empty or not a recognised name.
                        if (f.key === "ifscCode") {
                          const fromIfsc = v.trim().length === 11 ? bankNameForIfsc(v) : null;
                          if (fromIfsc && (!b.bankName || !isKnownBankName(b.bankName as string))) {
                            next.bankName = fromIfsc;
                          }
                        }
                        return next;
                      });
                    }}
                  />
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
      )}

      {/* The tab's single save — personal + bank in one go. */}
      <div className="flex justify-end">
        <Button onClick={onSave} disabled={saving}>{saving ? "Saving…" : "Save changes"}</Button>
      </div>
    </div>
  );
}

function ReadOnly({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <p className="text-xs text-gray-400">{label}</p>
      <p className="text-gray-800 break-words whitespace-normal">{value || "—"}</p>
    </div>
  );
}

function Field({ label, k, form, set, type = "text" }:
  { label: string; k: string; form: Record<string, any>; set: (k: string, v: string) => void; type?: string }) {
  return (
    <div>
      <Label className="text-xs text-gray-500">{label}</Label>
      <Input type={type} value={form[k] ?? ""} onChange={(e) => set(k, e.target.value)} />
    </div>
  );
}

function AddressField({ label, k, form, set }:
  { label: string; k: string; form: Record<string, any>; set: (k: string, v: string) => void }) {
  return (
    <div>
      <Label className="text-xs text-gray-500">{label}</Label>
      <textarea
        rows={2}
        value={form[k] ?? ""}
        onChange={(e) => set(k, e.target.value)}
        className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
      />
    </div>
  );
}

function PhoneField({ label, k, form, set }:
  { label: string; k: string; form: Record<string, any>; set: (k: string, v: string) => void }) {
  const val = form[k] ?? "";
  const invalid = val && !isValidPhone(val);
  return (
    <div>
      <Label className="text-xs text-gray-500">{label}</Label>
      <Input
        type="tel"
        inputMode="numeric"
        maxLength={10}
        value={val}
        onChange={(e) => {
          const v = e.target.value.replace(/\D/g, "");
          set(k, v);
        }}
        className={invalid ? "border-red-400 focus-visible:ring-red-400" : ""}
      />
      {invalid && (
        <p className="text-xs text-red-500 mt-1">Enter a valid 10-digit mobile number</p>
      )}
    </div>
  );
}

function GenderField({ label, k, form, set }:
  { label: string; k: string; form: Record<string, any>; set: (k: string, v: string) => void }) {
  const matched = GENDER_OPTIONS.find((g) => g.toLowerCase() === (form[k] ?? "").toLowerCase());
  return (
    <div>
      <Label className="text-xs text-gray-500">{label}</Label>
      <Select value={matched ?? undefined} onValueChange={(v) => set(k, v)}>
        <SelectTrigger>
          <SelectValue placeholder="Select gender" />
        </SelectTrigger>
        <SelectContent>
          {GENDER_OPTIONS.map((g) => (
            <SelectItem key={g} value={g}>{g}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function StateField({ label, k, form, set }:
  { label: string; k: string; form: Record<string, any>; set: (k: string, v: string) => void }) {
  const raw = form[k] ?? "";
  // numeric = old legacy ID → treat as unset; otherwise match by name (case-insensitive)
  const matched = /^\d+$/.test(raw)
    ? undefined
    : INDIAN_STATES.find((s) => s.toLowerCase() === raw.toLowerCase());
  return (
    <div>
      <Label className="text-xs text-gray-500">{label}</Label>
      <Select value={matched ?? undefined} onValueChange={(v) => set(k, v)}>
        <SelectTrigger>
          <SelectValue placeholder="Select state" />
        </SelectTrigger>
        <SelectContent>
          {INDIAN_STATES.map((s) => (
            <SelectItem key={s} value={s}>{s}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
